/** Home — LOTE M. URL: /home. Refresco visual + CTA informativo 2026-08-08. */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shirt, Camera, Sparkles, CalendarDays, UserRoundPlus, Wand2, Apple, Play } from 'lucide-react';
import { useWww } from './_layout';
import { wwwTheme, wwwRadius, wwwFonts, wwwAccent } from '@/src/website/theme';
import { SeoHead, organizationJsonLd, websiteJsonLd, softwareApplicationJsonLd } from '@/src/website/SeoHead';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@/src/website/seo';

// Web equivalent of RN's useWindowDimensions().
function TravelVideo() {
  const { width } = useWindowDimensions();
  const isMobile = width < 760;
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  // Defer the video entirely until the section scrolls into view (zero impact
  // on initial render / LCP; only the lightweight WebP poster ships before).
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: isMobile ? '78%' : 300, maxWidth: 320, aspectRatio: '9 / 16', borderRadius: wwwRadius.xl, overflow: 'hidden', border: `1px solid ${wwwTheme.border}`, flexShrink: 0, backgroundColor: wwwTheme.surface, alignSelf: 'center' }}>
      <video
        data-testid="travel-video"
        src={visible ? '/travel-story.mp4' : undefined}
        poster="/travel-poster.webp"
        preload="none"
        muted
        loop
        playsInline
        autoPlay={visible}
        onLoadedData={(e) => { e.currentTarget.play().catch(() => {}); }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
}

function useWindowDimensions() {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));
  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

export default function Home() {
  const { t, lang } = useWww();
  const { width } = useWindowDimensions();
  const isMobile = width < 760;
  const feats = [
    { icon: Shirt,        t: t.homeF1Title, b: t.homeF1Body },
    { icon: Camera,       t: t.homeF2Title, b: t.homeF2Body },
    { icon: Sparkles,     t: t.homeF3Title, b: t.homeF3Body },
    { icon: CalendarDays, t: t.homeF4Title, b: t.homeF4Body },
  ];
  const steps = [
    { icon: UserRoundPlus, text: t.homeHow1 },
    { icon: Camera,        text: t.homeHow2 },
    { icon: Wand2,         text: t.homeHow3 },
  ];
  // Combine three Schema.org blocks into a single @graph for cleaner JSON-LD.
  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [organizationJsonLd(), websiteJsonLd(), softwareApplicationJsonLd()],
  };

  // Web replacement for <Stack.Screen options={{ title: ... }} />: update
  // document.title so the browser tab matches the string the native screen
  // header used in the Expo version. Runs after SeoHead's effect so it has
  // the final word, exactly as in the original render order.
  useEffect(() => {
    document.title = 'STILGIA · ' + t.heroTitle;
  }, [t.heroTitle]);

  return (
    <>
      <SeoHead page="home" locale={lang} jsonLd={homeJsonLd} />

      {/* ============================== HERO ============================== */}
      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingTop: 48, paddingBottom: 80, maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 40 : 56, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', color: wwwAccent, fontSize: 13, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase' }}>{t.heroPretitle}</span>
          <h1 data-testid="hero-title" style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: isMobile ? 40 : 58, lineHeight: isMobile ? '46px' : '64px', marginTop: 16, marginBottom: 0, maxWidth: 680 }}>{t.heroTitle}</h1>
          <p style={{ color: wwwTheme.muted, fontSize: isMobile ? 16 : 18, lineHeight: isMobile ? '24px' : '28px', marginTop: 18, marginBottom: 0, maxWidth: 560 }}>{t.heroSubtitle}</p>
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 28 }}>
            <Link to="/pricing" data-testid="hero-cta-pricing" style={{ textDecoration: 'none', backgroundColor: wwwTheme.text, borderRadius: wwwRadius.pill, paddingLeft: 24, paddingRight: 24, paddingTop: 13, paddingBottom: 13 }}>
              <span style={{ color: wwwTheme.onPrimary, fontWeight: 600, fontSize: 14 }}>{t.heroCtaPrimary}</span>
            </Link>
            <Link to="/features" data-testid="hero-cta-features" style={{ textDecoration: 'none', borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.pill, paddingLeft: 24, paddingRight: 24, paddingTop: 12, paddingBottom: 12 }}>
              <span style={{ color: wwwTheme.text, fontWeight: 600, fontSize: 14 }}>{t.heroCtaSecondary}</span>
            </Link>
          </div>
          <p data-testid="hero-stores-badge" style={{ color: wwwTheme.muted, fontSize: 12, marginTop: 22, marginBottom: 0, letterSpacing: '0.4px' }}>{t.heroBadge}</p>
        </div>
        <div style={{ width: isMobile ? '100%' : 400, flexShrink: 0 }}>
          <img
            data-testid="hero-editorial-image"
            src="/hero-editorial.jpg"
            alt="STILGIA — armario digital con prueba virtual mediante IA"
            style={{ width: '100%', height: isMobile ? 380 : 500, objectFit: 'cover', borderRadius: wwwRadius.xl, border: `1px solid ${wwwTheme.border}`, display: 'block' }}
          />
        </div>
      </div>

      {/* ============================ FEATURES ============================ */}
      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingTop: 64, paddingBottom: 64, maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' }}>
        <h2 style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: isMobile ? 30 : 40, marginTop: 0, marginBottom: 10 }}>{t.homeFeaturesTitle}</h2>
        <p style={{ color: wwwTheme.muted, fontSize: 16, marginTop: 0, marginBottom: 36 }}>{t.homeFeaturesSubtitle}</p>
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 18 }}>
          {feats.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} data-testid={`home-feature-card-${i + 1}`} style={{ width: isMobile ? '100%' : 'calc(50% - 9px)', backgroundColor: wwwTheme.surface, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.lg, padding: 24, boxSizing: 'border-box' }}>
                <div style={{ width: 40, height: 40, borderRadius: wwwRadius.md, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, boxSizing: 'border-box' }}>
                  <Icon size={19} color={wwwTheme.text} strokeWidth={1.6} />
                </div>
                <h3 style={{ color: wwwTheme.text, fontSize: 18, fontWeight: 600, marginTop: 0, marginBottom: 8 }}>{f.t}</h3>
                <p style={{ color: wwwTheme.muted, fontSize: 14, lineHeight: '21px', marginTop: 0, marginBottom: 0 }}>{f.b}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* =========================== HOW IT WORKS ========================= */}
      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingTop: 64, paddingBottom: 64, maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' }}>
        <h2 style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: isMobile ? 30 : 40, marginTop: 0, marginBottom: 32 }}>{t.homeHowTitle}</h2>
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} data-testid={`home-how-step-${i + 1}`} style={{ display: 'flex', flexDirection: 'row', gap: 16, marginBottom: 18, alignItems: 'flex-start' }}>
              <div style={{ width: 40, height: 40, borderRadius: wwwRadius.pill, borderWidth: 1, borderStyle: 'solid', borderColor: wwwTheme.border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box', position: 'relative' }}>
                <Icon size={17} color={wwwTheme.text} strokeWidth={1.6} />
                <span style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: 999, backgroundColor: wwwTheme.text, color: wwwTheme.onPrimary, fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
              </div>
              <p style={{ color: wwwTheme.text, fontSize: 16, lineHeight: '24px', flex: 1, paddingTop: 8, marginTop: 0, marginBottom: 0 }}>{s.text}</p>
            </div>
          );
        })}
      </div>

      {/* ========================= TRAVEL BY STILGIA ====================== */}
      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingTop: 64, paddingBottom: 64, maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 36 : 64, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span data-testid="travel-tag" style={{ display: 'block', color: wwwAccent, fontSize: 13, fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase' }}>{t.travelTag}</span>
          <h2 data-testid="travel-claim" style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: isMobile ? 30 : 42, lineHeight: isMobile ? '36px' : '48px', marginTop: 14, marginBottom: 0, maxWidth: 560 }}>{t.travelClaim}</h2>
          <p style={{ color: wwwTheme.muted, fontSize: 16, lineHeight: '24px', marginTop: 16, marginBottom: 0, maxWidth: 520 }}>{t.travelBody}</p>
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[t.travelBullet1, t.travelBullet2].map((bl, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ color: wwwTheme.text, fontSize: 14 }}>✓</span>
                <span style={{ color: wwwTheme.text, fontSize: 14, lineHeight: '20px' }}>{bl}</span>
              </div>
            ))}
          </div>
          <p style={{ color: wwwTheme.muted, fontSize: 12, marginTop: 18, marginBottom: 0 }}>{t.travelPlansNote}</p>
          <Link to="/features#travel" data-testid="travel-cta" style={{ textDecoration: 'none', display: 'inline-block', backgroundColor: wwwTheme.text, borderRadius: wwwRadius.pill, paddingLeft: 24, paddingRight: 24, paddingTop: 13, paddingBottom: 13, marginTop: 24 }}>
            <span style={{ color: wwwTheme.onPrimary, fontWeight: 600, fontSize: 14 }}>{t.travelCta}</span>
          </Link>
          <p data-testid="travel-disclaimer" style={{ color: wwwTheme.muted, fontSize: 11, marginTop: 18, marginBottom: 0, fontStyle: 'italic' }}>{t.travelDisclaimer}</p>
        </div>
        <TravelVideo />
      </div>

      {/* ============================ FINAL CTA =========================== */}
      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingTop: 64, paddingBottom: 64, maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' }}>
        <div data-testid="home-final-cta" style={{ backgroundColor: wwwTheme.surface, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.xl, padding: isMobile ? 28 : 48, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <h2 style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: isMobile ? 26 : 34, marginTop: 0, marginBottom: 10 }}>{t.homeFinalCtaTitle}</h2>
          <p style={{ color: wwwTheme.muted, fontSize: 15, lineHeight: '22px', marginTop: 0, marginBottom: 24, maxWidth: 520 }}>{t.homeFinalCtaBody}</p>
          {/* Official store links — both listings verified publicly live. */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, alignSelf: 'stretch', flexWrap: 'wrap' }}>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="store-pill-appstore" style={{ textDecoration: 'none', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.pill, paddingLeft: 18, paddingRight: 18, paddingTop: 11, paddingBottom: 11, boxSizing: 'border-box' }}>
              <Apple size={16} color={wwwTheme.text} strokeWidth={1.6} />
              <span style={{ color: wwwTheme.text, fontSize: 13, fontWeight: 500 }}>{t.storeAppStoreSoon}</span>
            </a>
            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" data-testid="store-pill-googleplay" style={{ textDecoration: 'none', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.pill, paddingLeft: 18, paddingRight: 18, paddingTop: 11, paddingBottom: 11, boxSizing: 'border-box' }}>
              <Play size={15} color={wwwTheme.text} strokeWidth={1.6} />
              <span style={{ color: wwwTheme.text, fontSize: 13, fontWeight: 500 }}>{t.storeGooglePlaySoon}</span>
            </a>
            <Link to="/features" data-testid="final-cta-learn-more" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', backgroundColor: wwwTheme.text, borderRadius: wwwRadius.pill, paddingLeft: 20, paddingRight: 20, paddingTop: 11, paddingBottom: 11, boxSizing: 'border-box', width: 'fit-content' }}>
              <span style={{ color: wwwTheme.onPrimary, fontWeight: 600, fontSize: 13 }}>{t.heroCtaSecondary}</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
